export interface RateLimitConfig {
	limit: number;
	windowMs: number;
	persist?: boolean;
}

interface RateLimitState {
	attempts: number;
	windowStart: number;
}

const memoryStore = new Map<string, RateLimitState>();

function getState(key: string, persist: boolean): RateLimitState | null {
	if (persist) {
		const raw = localStorage.getItem(`rl:${key}`);
		if (!raw) return null;
		try {
			return JSON.parse(raw) as RateLimitState;
		} catch {
			return null;
		}
	}
	return memoryStore.get(key) ?? null;
}

function setState(key: string, state: RateLimitState, persist: boolean): void {
	if (persist) {
		localStorage.setItem(`rl:${key}`, JSON.stringify(state));
	} else {
		memoryStore.set(key, state);
	}
}

function removeState(key: string, persist: boolean): void {
	if (persist) {
		localStorage.removeItem(`rl:${key}`);
	} else {
		memoryStore.delete(key);
	}
}

export function checkAndRecord(
	key: string,
	config: RateLimitConfig,
): { allowed: boolean; retryAfter: number | null } {
	const { limit, windowMs, persist = false } = config;
	const now = Date.now();
	const current = getState(key, persist);

	if (!current || now - current.windowStart >= windowMs) {
		setState(key, { attempts: 1, windowStart: now }, persist);
		return { allowed: true, retryAfter: null };
	}

	if (current.attempts >= limit) {
		const retryAfter = windowMs - (now - current.windowStart);
		return { allowed: false, retryAfter };
	}

	setState(key, { ...current, attempts: current.attempts + 1 }, persist);
	return { allowed: true, retryAfter: null };
}

export function resetRateLimit(key: string, persist = false): void {
	removeState(key, persist);
}
